/**
 * Safe wrappers around localStorage/sessionStorage that silently catch errors.
 *
 * Storage APIs can throw in several scenarios:
 *  - Private/incognito browsing mode
 *  - Storage quota exceeded
 *  - Storage disabled by user/policy
 *  - SSR environments without a window
 *
 * These helpers match the existing silent-catch behavior while consolidating
 * the boilerplate into a single location. A future `onError` hook can be added
 * here to wire up Sentry/logging without touching call-sites.
 */

type StorageType = 'local' | 'session';

function getStorage(type: StorageType): Storage {
  return type === 'local' ? localStorage : sessionStorage;
}

/** Read a value from storage. Returns `null` when the key is missing or on error. */
export function safeGetItem(key: string, type: StorageType = 'local'): string | null {
  try {
    return getStorage(type).getItem(key);
  } catch {
    return null;
  }
}

/** Write a value to storage. Silently ignores errors (e.g. quota exceeded). */
export function safeSetItem(key: string, value: string, type: StorageType = 'local'): void {
  try {
    getStorage(type).setItem(key, value);
  } catch {
    // Storage write failed (quota exceeded, private mode, etc.)
  }
}

/** Remove a key from storage. Silently ignores errors. */
export function safeRemoveItem(key: string, type: StorageType = 'local'): void {
  try {
    getStorage(type).removeItem(key);
  } catch {
    // Storage removal failed
  }
}
